/**
 * @file LocationMultiSelect.js
 * @description A multi-select dropdown component for selecting locations.
 */

import React, { useState, useEffect } from "react";
import { SearchableDropdown } from "../../helpers/SearchableDropdown_style_b_v2";
import { fetchLocations } from "../../../services/orgLevelService";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/**
 * LocationMultiSelect component represents a searchable multi-select dropdown for selecting locations.
 *
 * @component
 * @param {string} className - Additional CSS class name for the component.
 * @param {Array} selectedLocations - Array of selected location objects.
 * @param {Function} onLocationsChange - Callback function triggered when the selected locations change.
 * @param {string} [label="Locations"] - Label text for the component.
 * @param {boolean} [isRequired=false] - Specifies whether the component is required or not.
 * @param {boolean} resetTrigger - Trigger for resetting the component.
 * @param {string} tenantId - ID of the tenant.
 * @param {boolean} [showSelectedItemsInField=true] - Specifies whether to show the selected items in the input field.
 * @param {boolean} [showSelectionBox=true] - Specifies whether to show the selection box.
 * @returns {JSX.Element} LocationMultiSelect component.
 */
const LocationMultiSelect = ({
  className,
  selectedLocations,
  onLocationsChange,
  label = "Locations",
  isRequired = false,
  resetTrigger,
  tenantId,
  showSelectedItemsInField = true,
  showSelectionBox = true,
  showSelectedCount = false,
}) => {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setIsLoading(true);
        const data = await fetchLocations(tenantId);
        setLocations(data);
      } catch (error) {
        console.error("Could not fetch locations:", error);
        Toast.error(getText('msg_664d39956d42'));
      } finally {
        setIsLoading(false);
      }
    };

    loadLocations();
  }, [tenantId]);

  const handleLocationsChange = (selectedOptions) => {
    onLocationsChange(selectedOptions);
  };

  return (
    <div className={`form-group mb-3 ${className}`}>
      <label htmlFor="location-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <SearchableDropdown
        id="location-dropdown"
        options={locations}
        optionIDField="id"
        updateOptions={handleLocationsChange}
        savedOptions={selectedLocations}
        savedOptionIDField="id"
        disabled={locations.length === 0}
        isLoading={isLoading}
        resetTrigger={resetTrigger}
        showSelectedItemsInField={showSelectedItemsInField}
        showSelectionBox={showSelectionBox}
        showSelectedCount={showSelectedCount}
        allSelectedLabel={getText('msg_e59e8f15f760')}
      />
    </div>
  );
};

export default LocationMultiSelect;
